import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { AtendimentoFormProps, Cliente } from '../../types';
import { useClientes } from '../../hooks/useClientes';
import { useAuth } from '../../hooks/useAuth';
import InputSearch from '../Common/InputSearch';

const AtendimentoForm: React.FC<AtendimentoFormProps> = ({
  show,
  onHide,
  onSave,
  funcionarios,
  modoEdicao = false,
  atendimentoParaEditar = null
}) => {
  const { user } = useAuth();
  const { clientes } = useClientes(user);

  const [formData, setFormData] = useState({
    clienteNome: '',
    clienteId: '',
    funcionarioId: '',
    tipoAtendimento: '',
    observacoes: '',
    resultado: '' as '' | 'finalizou_contrato' | 'nao_pagou' | 'nao_levou' | 'so_orcamento' | 'converteu_reserva' | 'outros'
  });


  const [clienteNovo, setClienteNovo] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  const tiposAtendimento = [
    { value: 'atendimento_escolher', label: 'Atendimento Escolher' },
    { value: 'atendimento_escolher_entrega', label: 'Atendimento Escolher/Entrega' },
    { value: 'entrega', label: 'Entrega' },
    { value: 'entrega_marcar_barra', label: 'Entrega/Marcar Barra' },
    { value: 'marcar_barra', label: 'Marcar Barra' },
    { value: 'escolher_infantil', label: 'Escolher Infantil' },
    { value: 'escolher', label: 'Escolher' },
    { value: 'orcamento', label: 'Orçamento' }
  ];

  useEffect(() => {
    if (modoEdicao && atendimentoParaEditar) {
      const newFormData = {
        clienteNome: atendimentoParaEditar.clienteNome,
        clienteId: atendimentoParaEditar.clienteId || '',
        funcionarioId: atendimentoParaEditar.funcionarioId,
        tipoAtendimento: atendimentoParaEditar.tipoAtendimento,
        observacoes: atendimentoParaEditar.observacoes || '',
        resultado: (atendimentoParaEditar.resultado || '') as '' | 'finalizou_contrato' | 'nao_pagou' | 'nao_levou' | 'so_orcamento' | 'converteu_reserva' | 'outros'
      };
      setFormData(newFormData);

      // Lógica corrigida: se tem clienteId é cliente cadastrado, senão é cliente novo
      const isClienteCadastrado = atendimentoParaEditar.clienteId && atendimentoParaEditar.clienteId.trim() !== '';
      setClienteNovo(!isClienteCadastrado);
    } else if (!show) {
      // Reset form quando fechar (modo normal)
      setFormData({
        clienteNome: '',
        clienteId: '',
        funcionarioId: '',
        tipoAtendimento: '',
        observacoes: '',
        resultado: ''
      });
      setClienteNovo(true);
      setError('');
    }
  }, [show, modoEdicao, atendimentoParaEditar?.id]);

  const handleClienteTipoChange = (tipo: 'novo' | 'existente') => {
    setClienteNovo(tipo === 'novo');
    setFormData(prev => ({
      ...prev,
      clienteNome: '',
      clienteId: ''
    }));
    setError('');
  };

  const handleClienteExistenteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const clienteId = e.target.value;
    const cliente = clientes.find(c => c.id === clienteId);

    setFormData(prev => ({
      ...prev,
      clienteId,
      clienteNome: cliente ? cliente.nome : ''
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validações
    if (!formData.clienteNome.trim()) {
      setError('Nome do cliente é obrigatório');
      return;
    }

    if (!formData.funcionarioId) {
      setError('Selecione o atendente');
      return;
    }

    if (!formData.tipoAtendimento) {
      setError('Selecione o tipo de atendimento');
      return;
    }

    // Para cliente existente, validar se foi selecionado
    if (!clienteNovo && !formData.clienteId) {
      setError('Selecione um cliente cadastrado');
      return;
    }

    setSaving(true);

    try {
      // Montar dados do atendimento (sem undefined para evitar erro no Firebase)
      const atendimentoData: any = {
        clienteNome: formData.clienteNome,
        funcionarioId: formData.funcionarioId,
        tipoAtendimento: formData.tipoAtendimento,
        observacoes: formData.observacoes
      };

      // Adicionar clienteId apenas se existir
      if (formData.clienteId) {
        atendimentoData.clienteId = formData.clienteId;
      }

      // Adicionar resultado apenas se existir (modo edição)
      if (formData.resultado && formData.resultado.trim()) {
        atendimentoData.resultado = formData.resultado;
      }

      await onSave(atendimentoData);
      onHide();
    } catch (err) {
      setError('Erro ao iniciar atendimento');
    } finally {
      setSaving(false);
    }
  };

  const funcionariosAtivos = funcionarios.filter(f => f.ativo);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton >
        <Modal.Title>{modoEdicao ? 'Editar Atendimento' : 'Iniciar Atendimento'}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Tipo de Cliente */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Tipo de Cliente</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                label="Cliente Novo"
                name="tipoCliente"
                checked={clienteNovo}
                onChange={() => handleClienteTipoChange('novo')}
              />
              <Form.Check
                type="radio"
                label="Cliente Cadastrado"
                name="tipoCliente"
                checked={!clienteNovo}
                onChange={() => handleClienteTipoChange('existente')}
              />
            </div>
          </Form.Group>

          <Row>
            <Col md={12}>
              {/* Cliente */}
              {clienteNovo ? (
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Nome do Cliente *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Digite o nome do cliente"
                    value={formData.clienteNome}
                    onChange={(e) => setFormData(prev => ({ ...prev, clienteNome: e.target.value }))}
                    required
                  />
                  <Form.Text className="text-muted">
                    Para atendimentos rápidos ou orçamentos, apenas o nome é necessário
                  </Form.Text>
                </Form.Group>
              ) : (
                <InputSearch
                  name="cliente"
                  data={clientes}
                  size={12}
                  value={formData.clienteId ? clientes.find(c => c.id === formData.clienteId) || null : null}
                  onChange={(cliente: Cliente) => {
                    setFormData(prev => ({
                      ...prev,
                      clienteId: cliente.id || '',
                      clienteNome: cliente.nome
                    }));
                  }}
                  onClear={() => setFormData(prev => ({ ...prev, clienteId: '' }))}
                  required
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              {/* Atendente/Funcionário */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Atendente *</Form.Label>
                <Form.Select
                  value={formData.funcionarioId}
                  onChange={(e) => setFormData(prev => ({ ...prev, funcionarioId: e.target.value }))}
                  required
                >
                  <option value="">Selecione o atendente...</option>
                  {funcionariosAtivos.map(funcionario => (
                    <option key={funcionario.id} value={funcionario.id}>
                      {funcionario.nome}
                    </option>
                  ))}
                </Form.Select>
                {funcionariosAtivos.length === 0 && (
                  <Form.Text className="text-danger">
                    Nenhum funcionário ativo cadastrado
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              {/* Tipo de Atendimento */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Tipo de Atendimento *</Form.Label>
                <Form.Select
                  value={formData.tipoAtendimento}
                  onChange={(e) => setFormData(prev => ({ ...prev, tipoAtendimento: e.target.value }))}
                  required
                >
                  <option value="">Selecione o tipo...</option>
                  {tiposAtendimento.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Observações */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Observações</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Observações sobre o atendimento (opcional)"
              value={formData.observacoes}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
            />
          </Form.Group>

          {/* Resultado - apenas em modo de edição */}
          {modoEdicao && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Resultado do Atendimento</Form.Label>
              <Form.Select
                value={formData.resultado}
                onChange={(e) => setFormData(prev => ({ ...prev, resultado: e.target.value as any }))}
              >
                <option value="">Selecione o resultado...</option>
                {/* <option value="finalizou_contrato">Finalizou Contrato</option> */}
                <option value="nao_pagou">Não Pagou</option>
                <option value="nao_levou">Não Levou</option>
                <option value="so_orcamento">Só Orçamento</option>
                <option value="converteu_reserva">Converteu Reserva</option>
                <option value="outros">Outros</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Selecione o resultado do atendimento
              </Form.Text>
            </Form.Group>
          )}

        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={saving || funcionariosAtivos.length === 0}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              {modoEdicao ? 'Salvando...' : 'Iniciando...'}
            </>
          ) : (
            <> {modoEdicao ? 'Salvar Alterações' : 'Iniciar Atendimento'}</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AtendimentoForm;

